import React, { useMemo, useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import Seo from './Seo';
import KwbLogo from './KwbLogo';
import { blogPosts } from '../generated/blogPosts';

const {
  FiArrowLeft,
  FiArrowUpRight,
  FiBookOpen,
  FiSearch,
  FiX,
  FiChevronDown,
} = FiIcons;

const PAGE_SIZE = 6;

const CATEGORY_ORDER = [
  'Leadership That Lasts',
  'Sales With Discipline',
  'Operational Excellence',
  'Customer Success',
  'Team Development',
];

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest first' },
  { value: 'oldest', label: 'Oldest first' },
  { value: 'reading', label: 'Shortest read' },
];

const BlogIndexPage = () => {
  const [query, setQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [activeTopic, setActiveTopic] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const categories = useMemo(() => {
    const present = new Set(blogPosts.filter((post) => post.slug !== 'complete-article-draft-template').map((post) => post.category).filter(Boolean));
    return CATEGORY_ORDER.filter((cat) => present.has(cat));
  }, []);

  const topics = useMemo(() => {
    const counts = {};
    blogPosts.filter((post) => post.slug !== 'complete-article-draft-template').forEach((post) => (post.topics || []).forEach((topic) => {
      counts[topic] = (counts[topic] || 0) + 1;
    }));
    return Object.keys(counts).sort((a, b) => counts[b] - counts[a] || a.localeCompare(b));
  }, []);

  const featured = useMemo(() => blogPosts.find((post) => post.slug !== 'complete-article-draft-template' && post.featured) || null, []);

  const filtered = useMemo(() => {
    let list = blogPosts.filter((post) => post.slug !== 'complete-article-draft-template');

    if (activeCategory !== 'all') {
      list = list.filter((post) => post.category === activeCategory);
    }
    if (activeTopic !== 'all') {
      list = list.filter((post) => (post.topics || []).includes(activeTopic));
    }
    const term = query.trim().toLowerCase();
    if (term) {
      list = list.filter((post) => {
        const haystack = [post.title, post.excerpt, post.category, ...(post.topics || [])]
          .join(' ')
          .toLowerCase();
        return haystack.includes(term);
      });
    }

    const sorted = [...list].sort((a, b) => {
      if (sortBy === 'oldest') return (a.date || '').localeCompare(b.date || '');
      if (sortBy === 'reading') return (a.readingMinutes || 0) - (b.readingMinutes || 0);
      return (b.date || '').localeCompare(a.date || '');
    });

    return sorted;
  }, [query, activeCategory, activeTopic, sortBy]);

  const visible = filtered.slice(0, visibleCount);
  const hasActiveFilters = query.trim() !== '' || activeCategory !== 'all' || activeTopic !== 'all';

  const resetFilters = () => {
    setQuery('');
    setActiveCategory('all');
    setActiveTopic('all');
  };

  const changeCategory = (category) => {
    setActiveCategory(category);
    setVisibleCount(PAGE_SIZE);
  };
  const changeTopic = (topic) => {
    setActiveTopic(topic);
    setVisibleCount(PAGE_SIZE);
  };
  const changeQuery = (value) => {
    setQuery(value);
    setVisibleCount(PAGE_SIZE);
  };
  const changeSort = (value) => {
    setSortBy(value);
    setVisibleCount(PAGE_SIZE);
  };

  const ArticleCard = ({ post, index }) => (
    <article className="group border-r border-y border-black">
      <a href={`/blog/${post.slug}/`} className="block h-full focus-visible:outline focus-visible:outline-4 focus-visible:outline-[#918147] focus-visible:outline-offset-[-4px]">
        <div className="aspect-[16/9] overflow-hidden bg-[#ded8c2]">
          <img src={post.cover} alt={post.coverAlt} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.025] transition-transform duration-700" />
        </div>
        <div className="p-7 md:p-10 min-h-[360px] flex flex-col">
          <div className="flex justify-between gap-5 text-[10px] font-bold uppercase tracking-[0.18em] text-[#4a4a4a]">
            <span>/ {String(index + 1).padStart(2, '0')} · {post.category}</span>
            <span>{post.readingMinutes} min read</span>
          </div>
          <div className="mt-auto pt-16">
            <h2 className="text-3xl md:text-5xl font-black uppercase tracking-tight leading-[0.92]">{post.title}</h2>
            <p className="mt-6 text-base leading-relaxed text-[#4a4a4a]">{post.excerpt}</p>
            <span className="mt-8 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] border-b border-black pb-1">
              Read the article <SafeIcon icon={FiArrowUpRight} />
            </span>
          </div>
        </div>
      </a>
    </article>
  );

  return (
    <div className="min-h-screen bg-white text-black">
      <Seo
        title="Leadership Insights | Katherine William-Botrous"
        description="Practical perspectives from Katherine William-Botrous on leadership, sales discipline, operations, customer success, and high-performance teams."
        path="/blog/"
      />

      <header className="bg-black text-white border-b border-white/15 px-5 md:px-10 py-6 md:py-8">
        <nav aria-label="Blog navigation" className="max-w-[1600px] mx-auto flex items-center justify-between gap-5">
          <a href="/#blog" className="group inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-white/70 hover:text-white transition-colors">
            <SafeIcon icon={FiArrowLeft} className="group-hover:-translate-x-1 transition-transform" />
            Back to website
          </a>
          <a href="/" className="inline-flex items-center focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#e2c96e]">
            <KwbLogo variant="white" className="h-9 w-auto md:h-10" />
          </a>
        </nav>
      </header>

      <main>
        <section className="bg-black text-white px-5 md:px-10 pt-20 md:pt-28 pb-24 md:pb-36">
          <div className="max-w-[1600px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <div className="text-xs font-bold tracking-[0.28em] uppercase text-[#e2c96e] mb-6">Thought leadership / Blog</div>
              <h1 className="text-[clamp(4rem,11vw,10rem)] font-black uppercase tracking-[-0.06em] leading-[0.76]">Ideas<br />in practice</h1>
            </div>
            <p className="lg:col-span-4 text-xl md:text-2xl leading-relaxed text-white/80">
              Practical lessons on building accountable teams, creating disciplined operating rhythms, and strengthening customer-centered organizations.
            </p>
          </div>
        </section>

        <section className="px-5 md:px-10 py-14 md:py-20">
          <div className="max-w-[1600px] mx-auto">
            {featured && !hasActiveFilters && (
              <div className="mb-12">
                <div className="text-xs font-bold tracking-[0.22em] uppercase text-[#918147] mb-6">Featured article</div>
                <article className="group grid grid-cols-1 lg:grid-cols-12 border border-black">
                  <a href={`/blog/${featured.slug}/`} className="lg:col-span-7 block overflow-hidden bg-[#ded8c2]">
                    <img src={featured.cover} alt={featured.coverAlt} loading="lazy" className="w-full h-full object-cover aspect-[16/9] lg:aspect-auto lg:h-full group-hover:scale-[1.02] transition-transform duration-700" />
                  </a>
                  <div className="lg:col-span-5 p-7 md:p-12 flex flex-col justify-center">
                    <div className="flex justify-between gap-5 text-[10px] font-bold uppercase tracking-[0.18em] text-[#4a4a4a]">
                      <span>· {featured.category}</span>
                      <span>{featured.readingMinutes} min read</span>
                    </div>
                    <h2 className="mt-6 text-3xl md:text-5xl font-black uppercase tracking-tight leading-[0.92]">{featured.title}</h2>
                    <p className="mt-6 text-base leading-relaxed text-[#4a4a4a]">{featured.excerpt}</p>
                    <a href={`/blog/${featured.slug}/`} className="mt-8 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] border-b border-black pb-1 self-start focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#918147]">
                      Read the article <SafeIcon icon={FiArrowUpRight} />
                    </a>
                  </div>
                </article>
              </div>
            )}

            <div className="mb-10 flex flex-col gap-6 border-y border-black py-6">
              <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_200px] gap-4">
                <label className="relative block">
                  <span className="sr-only">Search articles</span>
                  <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-lg text-[#4a4a4a]" />
                  <input
                    type="search"
                    value={query}
                    onChange={(event) => changeQuery(event.target.value)}
                    placeholder="Search articles by title or topic"
                    className="w-full h-12 border border-black pl-12 pr-11 text-sm bg-transparent outline-none focus:ring-2 focus:ring-[#918147] placeholder:text-[#8a8a8a]"
                    aria-label="Search articles"
                  />
                  {query && (
                    <button
                      type="button"
                      onClick={() => changeQuery('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-lg text-[#4a4a4a] hover:text-black"
                      aria-label="Clear search"
                    >
                      <SafeIcon icon={FiX} />
                    </button>
                  )}
                </label>
                <label className="relative block">
                  <span className="sr-only">Sort articles</span>
                  <select
                    value={sortBy}
                    onChange={(event) => changeSort(event.target.value)}
                    className="w-full h-12 border border-black pl-4 pr-10 text-sm font-bold bg-white appearance-none outline-none focus:ring-2 focus:ring-[#918147] cursor-pointer"
                    aria-label="Sort articles"
                  >
                    {SORT_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                  <SafeIcon icon={FiChevronDown} className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-[#4a4a4a]" />
                </label>
              </div>

              {categories.length > 0 && (
                <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Filter by category">
                  <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#4a4a4a] mr-1">Category</span>
                  <button
                    type="button"
                    onClick={() => changeCategory('all')}
                    className={`px-4 py-2 text-xs font-bold uppercase tracking-[0.12em] border transition-colors ${
                      activeCategory === 'all'
                        ? 'bg-black text-white border-black'
                        : 'border-black text-black hover:bg-[#f7f5ef]'
                    }`}
                  >
                    All
                  </button>
                  {categories.map((category) => (
                    <button
                      key={category}
                      type="button"
                      onClick={() => changeCategory(category)}
                      aria-pressed={activeCategory === category}
                      className={`px-4 py-2 text-xs font-bold uppercase tracking-[0.12em] border transition-colors ${
                        activeCategory === category
                          ? 'bg-black text-white border-black'
                          : 'border-black text-black hover:bg-[#f7f5ef]'
                      }`}
                    >
                      {category}
                    </button>
                  ))}
                </div>
              )}

              {topics.length > 0 && (
                <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Filter by topic">
                  <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#4a4a4a] mr-1">Topic</span>
                  <button
                    type="button"
                    onClick={() => changeTopic('all')}
                    className={`px-3 py-1.5 text-[11px] font-semibold rounded-full border transition-colors ${
                      activeTopic === 'all'
                        ? 'bg-black text-white border-black'
                        : 'border-black/30 text-black hover:bg-[#f7f5ef]'
                    }`}
                  >
                    All topics
                  </button>
                  {topics.map((topic) => (
                    <button
                      key={topic}
                      type="button"
                      onClick={() => changeTopic(topic)}
                      aria-pressed={activeTopic === topic}
                      className={`px-3 py-1.5 text-[11px] font-semibold rounded-full border transition-colors ${
                        activeTopic === topic
                          ? 'bg-black text-white border-black'
                          : 'border-black/30 text-black hover:bg-[#f7f5ef]'
                      }`}
                    >
                      {topic}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="mb-8 flex items-center justify-between gap-4 text-xs font-bold uppercase tracking-[0.16em] text-[#4a4a4a]">
              <span>
                {filtered.length} {filtered.length === 1 ? 'article' : 'articles'}
                {hasActiveFilters && ' shown'}
              </span>
              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={resetFilters}
                  className="inline-flex items-center gap-2 underline underline-offset-4 hover:text-black"
                >
                  Clear all filters <SafeIcon icon={FiX} />
                </button>
              )}
            </div>

            {visible.length ? (
              <div className="grid grid-cols-1 md:grid-cols-2 border-l border-black">
                {visible.map((post, index) => (
                  <ArticleCard key={post.slug} post={post} index={index} />
                ))}
              </div>
            ) : (
              <div className="border border-black min-h-[430px] p-8 md:p-14 flex flex-col md:flex-row items-start md:items-end justify-between gap-12">
                <div className="max-w-3xl">
                  <div className="text-xs font-bold tracking-[0.22em] uppercase text-[#918147] mb-6">No matches</div>
                  <h2 className="text-5xl md:text-7xl font-black uppercase tracking-tighter leading-[0.84]">
                    Nothing matches those filters.
                  </h2>
                  <p className="mt-8 text-lg md:text-xl leading-relaxed text-[#4a4a4a]">
                    Try a different keyword, category, or topic — or clear the filters to browse everything.
                  </p>
                  <button
                    type="button"
                    onClick={resetFilters}
                    className="mt-8 inline-flex items-center gap-3 bg-black text-white px-6 py-4 text-sm font-black uppercase tracking-[0.14em] hover:bg-[#71642f] transition-colors"
                  >
                    Clear all filters <SafeIcon icon={FiX} />
                  </button>
                </div>
                <SafeIcon icon={FiBookOpen} className="text-6xl text-[#918147]" />
              </div>
            )}

            {visible.length < filtered.length && (
              <div className="mt-12 text-center">
                <button
                  type="button"
                  onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}
                  className="inline-flex items-center gap-2 bg-black text-white px-8 py-4 text-sm font-black uppercase tracking-[0.14em] hover:bg-[#71642f] transition-colors"
                >
                  Load more ({filtered.length - visible.length} remaining)
                </button>
              </div>
            )}
          </div>
        </section>
      </main>
    </div>
  );
};

export default BlogIndexPage;
